export const MANUAL_ACCOUNT_PREFIX = "manual:";

const MANUAL_ACCOUNT_SELECTION_INITIALIZED_VALUES_KEY =
  "manualAccountSelectionInitializedValues";
const ACCOUNT_SELECTION_KNOWN_VALUES_KEY = "accountSelectionKnownValues";

type AccountOptionLike = {
  value: string;
};

function readStoredValues(
  storage: Storage,
  key: string
): { values: Set<string>; exists: boolean; valid: boolean } {
  const storedValue = storage.getItem(key);
  if (storedValue == null) {
    return { values: new Set(), exists: false, valid: true };
  }

  try {
    const parsed = JSON.parse(storedValue);
    return {
      values: new Set(Array.isArray(parsed) ? parsed.filter(Boolean) : []),
      exists: true,
      valid: true,
    };
  } catch {
    return { values: new Set(), exists: true, valid: false };
  }
}

function writeStoredValues(storage: Storage, key: string, values: Set<string>) {
  storage.setItem(key, JSON.stringify([...values]));
}

export function isManualAccountOption(account: AccountOptionLike): boolean {
  return account.value.startsWith(MANUAL_ACCOUNT_PREFIX);
}

function toManualAccountValue(sourceOrValue: string): string {
  return sourceOrValue.startsWith(MANUAL_ACCOUNT_PREFIX)
    ? sourceOrValue
    : `${MANUAL_ACCOUNT_PREFIX}${sourceOrValue}`;
}

/**
 * Forget that the given manual account source(s) were ever auto-selected, so the
 * default-selection logic treats them as freshly available again. Called right
 * after a manual transaction is created so the matching manual account gets
 * auto-selected in cashflow and budget — even if the user had previously seen
 * (and de-selected) that source.
 *
 * Accepts either bare sources ("cash") or full option values ("manual:cash").
 */
export function clearManualAccountInitialization(
  sources: string[],
  storage: Storage
): void {
  if (sources.length === 0) return;
  const { values, exists } = readStoredValues(
    storage,
    MANUAL_ACCOUNT_SELECTION_INITIALIZED_VALUES_KEY
  );
  if (!exists) return;

  let changed = false;
  for (const source of sources) {
    if (values.delete(toManualAccountValue(source))) changed = true;
  }

  if (changed) {
    writeStoredValues(
      storage,
      MANUAL_ACCOUNT_SELECTION_INITIALIZED_VALUES_KEY,
      values
    );
  }
}

export function includeDefaultManualAccountSelections<
  T extends AccountOptionLike
>(selectedAccounts: T[], availableAccounts: T[], storage: Storage): T[] {
  const manualAccounts = availableAccounts.filter(isManualAccountOption);
  if (manualAccounts.length === 0) return selectedAccounts;

  const initializedValues = readStoredValues(
    storage,
    MANUAL_ACCOUNT_SELECTION_INITIALIZED_VALUES_KEY
  ).values;
  const selectedValues = new Set(
    selectedAccounts.map((account) => account.value)
  );
  const manualAccountsToDefaultSelect = manualAccounts.filter(
    (account) =>
      !selectedValues.has(account.value) &&
      !initializedValues.has(account.value)
  );

  manualAccounts.forEach((account) => initializedValues.add(account.value));
  writeStoredValues(
    storage,
    MANUAL_ACCOUNT_SELECTION_INITIALIZED_VALUES_KEY,
    initializedValues
  );

  if (manualAccountsToDefaultSelect.length === 0) return selectedAccounts;

  return [...selectedAccounts, ...manualAccountsToDefaultSelect];
}

export function includeDefaultAccountSelections<T extends AccountOptionLike>(
  selectedAccounts: T[],
  availableAccounts: T[],
  storage: Storage
): T[] {
  const selectedWithManualDefaults = includeDefaultManualAccountSelections(
    selectedAccounts,
    availableAccounts,
    storage
  );
  const selectedValues = new Set(
    selectedWithManualDefaults.map((account) => account.value)
  );
  const knownSnapshot = readStoredValues(
    storage,
    ACCOUNT_SELECTION_KNOWN_VALUES_KEY
  );
  const knownValues = knownSnapshot.values;
  const hasKnownAccountSnapshot = knownSnapshot.exists && knownSnapshot.valid;
  const newlyAvailableAccounts = hasKnownAccountSnapshot
    ? availableAccounts.filter(
        (account) =>
          !selectedValues.has(account.value) && !knownValues.has(account.value)
      )
    : [];

  availableAccounts.forEach((account) => knownValues.add(account.value));
  writeStoredValues(storage, ACCOUNT_SELECTION_KNOWN_VALUES_KEY, knownValues);

  if (newlyAvailableAccounts.length === 0) {
    return selectedWithManualDefaults;
  }

  return [...selectedWithManualDefaults, ...newlyAvailableAccounts];
}
